import { Flex, List, Text } from '@chakra-ui/react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, { FC, useMemo } from 'react';
import { Crawler } from 'types/crawler';
import { MenuCrawler } from 'types/menuCrawler';
import { CrawlerAddButton } from '../CrawlerAddButton/CrawlerAddButton';
import { CrawlersListItem } from '../CrawlerListItem';

type Props = {
  menuCrawlers: MenuCrawler[];
  crawlers: Crawler[];
  parentId: number;
  depth: number;
  update: boolean;
  handleClickAdd: (id: number, parentId: number) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDelete: (id: number, parentId: number) => void;
};

// ネストリストの最大の深さ
const MAX_DEPTH = 5;

/**
 * 並び替え用の関数
 * @param a
 * @param b
 * @returns
 */
const sortByDisplayOrder = (a: MenuCrawler, b: MenuCrawler) =>
  a.displayOrder - b.displayOrder;

/**
 * 指定した親の子クローラーを取得する。
 * 親が -1 の場合は、ルートのクローラーを取得する。
 * @param menuCrawlers
 * @param parentId
 * @returns
 */
const getCrawlers = (menuCrawlers: MenuCrawler[], parentId: number) => {
  if (parentId === -1) {
    return menuCrawlers
      .filter((menuCrawler) => menuCrawler.firstCrawler)
      .sort(sortByDisplayOrder);
  }

  const parent = menuCrawlers.find(
    (menuCrawler) => menuCrawler.crawlerId === parentId,
  );
  if (parent) {
    return menuCrawlers
      .filter((menuCrawler) =>
        parent.targetCrawlers?.includes(menuCrawler.crawlerId),
      )
      .sort(sortByDisplayOrder);
  }

  return [];
};

export const CrawlerList: FC<Props> = ({
  menuCrawlers,
  crawlers,
  parentId,
  depth,
  update,
  handleClickAdd,
  handleDragEnd,
  handleDelete,
}) => {
  const siblings = useMemo(
    () => getCrawlers(menuCrawlers, parentId),
    [menuCrawlers, parentId],
  );
  const marginLeft = useMemo(() => {
    if (depth === 0) {
      return '0px';
    }
    if (depth === MAX_DEPTH - 1) {
      return '72px';
    }

    return '40px';
  }, [depth]);

  if (depth === 0 && siblings.length === 0) {
    return (
      <Flex gap="8px" align="center">
        {!update && (
          <CrawlerAddButton
            id={-1}
            parentId={-1}
            crawlers={crawlers}
            visible
            toChild={false}
            onClick={handleClickAdd}
          />
        )}
        <Text color="gray.500" fontSize="sm">
          このメニューに登録されているクローラーはありません
        </Text>
      </Flex>
    );
  }

  return (
    <List ml={marginLeft}>
      <DndContext
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={siblings.map((s) => s.crawlerId)}
          strategy={verticalListSortingStrategy}
        >
          {siblings.map((sibling) => {
            const name =
              crawlers.find((crawler) => crawler.id === sibling.crawlerId)
                ?.name || '';

            return (
              <React.Fragment key={`${depth}-${sibling.crawlerId}`}>
                <CrawlersListItem
                  id={sibling.crawlerId}
                  name={name}
                  parentId={parentId}
                  crawlers={crawlers}
                  update={update}
                  canAddChild={depth < MAX_DEPTH - 1}
                  handleClickAdd={handleClickAdd}
                  handleDelete={handleDelete}
                />
                {!!sibling.targetCrawlers?.length && depth < MAX_DEPTH - 1 && (
                  <CrawlerList
                    menuCrawlers={menuCrawlers}
                    crawlers={crawlers}
                    parentId={sibling.crawlerId}
                    depth={depth + 1}
                    update={update}
                    handleClickAdd={handleClickAdd}
                    handleDragEnd={handleDragEnd}
                    handleDelete={handleDelete}
                  />
                )}
              </React.Fragment>
            );
          })}
        </SortableContext>
      </DndContext>
    </List>
  );
};
